import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Alert, Button, Modal, QRCode, Radio, Spin, message } from 'antd';
import { AlipayCircleOutlined, ReloadOutlined, WechatOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import { get, post } from '../../../../axios/axios';
import { alipayGuideHref, isWechatBrowser, openAlipay } from '../../../../components/PayQRCodes';
import { useDevice } from '../../../../hooks/useDevice';
import './index.scss';

/** 手机支付宝付完 / 取消后回到套餐页，带上订单号，套餐页据此查单并打开「我的套餐」显示兑换码 */
export const packageReturnPath = (orderNo) => `/eightPieceSet?paid=${encodeURIComponent(orderNo || '')}`;

const EightPayModal = NiceModal.create(
    ({ orderNo, majorName, price, packageType = 11, zIndex = 1892, preserveContext = false }) => {
        const { visible, hide, resolve } = useModal();
        const { isMobile } = useDevice();
        const [payType, setPayType] = useState(isMobile ? 2 : 1);
        const [payInfo, setPayInfo] = useState(null);
        const [loading, setLoading] = useState(true);
        const [checking, setChecking] = useState(false);
        const [paymentError, setPaymentError] = useState('');
        const [statusMessage, setStatusMessage] = useState('');
        const actionsRef = useRef({});

        useEffect(() => {
            if (!visible) return undefined;

            // Each opening owns its requests and timer. Late responses cannot update a later order.
            let active = true;
            let settled = false;
            let timer = null;
            let checkingNow = false;
            let requestSequence = 0;
            const initialType = isMobile ? 2 : 1;
            setPayType(initialType);
            setPayInfo(null);
            setPaymentError('');
            setStatusMessage('');
            setChecking(false);

            const stopPolling = () => {
                if (timer !== null) window.clearTimeout(timer);
                timer = null;
            };

            const scheduleCheck = (delay = 2000) => {
                stopPolling();
                if (active && !settled) {
                    timer = window.setTimeout(() => checkPayment(false), delay);
                }
            };

            const finishPayment = (data) => {
                if (!active || settled) return;
                settled = true;
                stopPolling();
                active = false;
                resolve({ ...data, orderNo, paid: true });
                hide();
            };

            const checkPayment = async (manual = false) => {
                if (!active || settled || checkingNow) return;
                checkingNow = true;
                stopPolling();
                if (manual) setChecking(true);
                let delay = 2000;
                try {
                    const res = await get(
                        `/member/payment/pay/result/${encodeURIComponent(orderNo)}`,
                        undefined,
                        { slient: true },
                    );
                    if (!active || settled) return;
                    if (res?.data?.code === 0) {
                        finishPayment(res.data);
                    } else {
                        setStatusMessage('等待支付完成，完成后将自动打开我的套餐。');
                        if (manual) message.info('暂未确认支付成功，稍后会自动继续检查');
                    }
                } catch (error) {
                    if (!active) return;
                    // A transport error says nothing about whether the user has paid.
                    setStatusMessage('支付状态暂未同步，正在自动重试。已付款请勿重复支付。');
                    delay = 4000;
                } finally {
                    checkingNow = false;
                    if (active && !settled) {
                        setChecking(false);
                        scheduleCheck(delay);
                    }
                }
            };

            const requestPayment = async (nextType) => {
                const sequence = ++requestSequence;
                stopPolling();
                setPayType(nextType);
                setPayInfo(null);
                setPaymentError('');
                setLoading(true);
                try {
                    const body = {
                        orderNo,
                        client: isMobile ? 'H5' : 'PC',
                        payType: nextType,
                        type: packageType,
                    };
                    // 手机支付宝是整页跳走的，不传回跳地址后端默认回首页，付完就看不到兑换码了
                    if (isMobile && nextType === 2) body.returnUrl = packageReturnPath(orderNo);
                    const res = await post('/member/payment/pay', body, { slient: true });
                    if (!active || settled || sequence !== requestSequence) return;
                    if (!res.success || !(nextType === 1 ? res.data?.codeUrl : res.data?.form)) {
                        throw new Error(res.msg || '暂时无法获取支付信息');
                    }
                    setPayInfo(res.data);
                    setStatusMessage('等待支付完成，完成后将自动打开我的套餐。');
                } catch (error) {
                    if (active && !settled && sequence === requestSequence) {
                        setPaymentError(error.message || '暂时无法获取支付信息，请刷新重试');
                    }
                } finally {
                    if (active && !settled && sequence === requestSequence) {
                        setLoading(false);
                        // Restart after retries and payment-method changes, for either provider.
                        scheduleCheck();
                    }
                }
            };

            actionsRef.current = {
                requestPayment,
                checkPayment,
                cancel: () => {
                    if (settled) return;
                    settled = true;
                    active = false;
                    stopPolling();
                    resolve({ cancelled: true });
                    hide();
                },
            };
            requestPayment(initialType);

            return () => {
                active = false;
                requestSequence += 1;
                stopPolling();
                actionsRef.current = {};
            };
        }, [visible, orderNo, packageType, isMobile, hide, resolve]);

        // 微信里打不开支付宝，进中转页引导去浏览器付款；其它手机浏览器当前页直接跳支付宝
        const inWechat = isWechatBrowser();
        const startMobilePayment = () => {
            if (!payInfo?.form) return;
            if (inWechat) openAlipay(payInfo.form, { orderNo });
            else window.location.assign(payInfo.form);
        };
        const mobileHref = !payInfo?.form ? undefined : inWechat ? alipayGuideHref(payInfo.form, orderNo) : payInfo.form;
        const displayedPrice = Number(price).toLocaleString('zh-CN', { maximumFractionDigits: 2 });

        return (
            <>
                <Modal
                    title="确认套餐订单"
                    open={visible}
                    centered
                    width={460}
                    zIndex={zIndex}
                    footer={null}
                    onCancel={() => actionsRef.current.cancel?.()}
                    maskClosable={false}
                    className="package-payment-modal"
                >
                    <div className="package-payment__summary">
                        <div className="package-payment__name">{majorName || '灵感长文八件套'}</div>
                        <div className="package-payment__price">
                            <span>¥</span>
                            {displayedPrice}
                        </div>
                        <div className="package-payment__order">
                            订单号：<span>{orderNo}</span>
                        </div>
                    </div>
                    {!isMobile && (
                        <Radio.Group
                            className="package-payment__methods"
                            optionType="button"
                            buttonStyle="solid"
                            value={payType}
                            onChange={(event) =>
                                actionsRef.current.requestPayment?.(event.target.value)
                            }
                        >
                            <Radio.Button value={1}>
                                <WechatOutlined /> 微信支付
                            </Radio.Button>
                            <Radio.Button value={2}>
                                <AlipayCircleOutlined /> 支付宝
                            </Radio.Button>
                        </Radio.Group>
                    )}

                    <div className="package-payment__code">
                        {loading ? (
                            <Spin tip="正在获取支付信息">
                                <div className="package-payment__loading" />
                            </Spin>
                        ) : paymentError ? (
                            <div className="package-payment__error">
                                <Alert type="info" showIcon message={paymentError} />
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={() => actionsRef.current.requestPayment?.(payType)}
                                >
                                    重新获取支付信息
                                </Button>
                            </div>
                        ) : isMobile ? (
                            <div className="package-payment__mobile">
                                <AlipayCircleOutlined />
                                <h3>使用支付宝完成支付</h3>
                                <p>
                                    {inWechat
                                        ? '微信里不能直接打开支付宝，点按钮后按提示在浏览器中付款。'
                                        : preserveContext
                                          ? '支付在新窗口打开，完成后返回本页继续当前创作。'
                                          : '支付后返回页面，即可查看套餐权益。'}
                                </p>
                                <Button
                                    type="primary"
                                    size="large"
                                    block
                                    href={preserveContext ? mobileHref : undefined}
                                    target={preserveContext && !inWechat ? '_blank' : undefined}
                                    rel={preserveContext && !inWechat ? 'noopener noreferrer' : undefined}
                                    onClick={preserveContext ? undefined : startMobilePayment}
                                >
                                    前往支付宝支付
                                </Button>
                            </div>
                        ) : payType === 1 && payInfo?.codeUrl ? (
                            <>
                                <QRCode value={payInfo.codeUrl} size={204} />
                                <p>请使用微信扫一扫</p>
                            </>
                        ) : payInfo?.form ? (
                            <>
                                <iframe
                                    src={payInfo.form}
                                    title="支付宝支付"
                                    className="package-payment__alipay"
                                />
                                <p>请使用支付宝完成支付</p>
                            </>
                        ) : null}
                    </div>

                    <p className="package-payment__status" role="status">
                        {statusMessage}
                    </p>
                    <div className="package-payment__actions">
                        <Button
                            onClick={() => actionsRef.current.checkPayment?.(true)}
                            loading={checking}
                        >
                            我已支付，查看结果
                        </Button>
                        {!loading && !paymentError && (
                            <Button
                                type="text"
                                icon={<ReloadOutlined />}
                                onClick={() => actionsRef.current.requestPayment?.(payType)}
                            >
                                刷新支付
                            </Button>
                        )}
                    </div>
                    <p className="package-payment__agreement">
                        支付即表示同意{' '}
                        <a href="/agreement" target="_blank" rel="noreferrer">
                            《服务协议》
                        </a>
                    </p>
                </Modal>
            </>
        );
    },
);

export default EightPayModal;
